import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RotatingCircle from "../../../components/RotatingCircle";
import { bearingDegForPatrolSegment } from "./patrolFloorPins";

const ZOOM_MIN = 1;
const ZOOM_MAX = 10;
const ZOOM_STEP = 0.25;
const SLIDER_STEP = 0.05;
const ZOOM_ANIMATION_DURATION_MS = 320;
const PATROL_SLIDE_DURATION_MS = 3000;

/** Smooth 0→1 easing (matches zoom RAF easing). */
const easeInOutSmooth = (t) => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

const BASE_PIN_SIZE = 20;
const BASE_ROUTE_LINE_STROKE = 3;
const BASE_ROUTE_ARROW_STROKE_THIN = 2;
const BASE_ROUTE_ARROW_STROKE_THICK = 3;
const BASE_START_DOT_R = 6;
const BASE_MID_CIRCLE_R = 10;
const BASE_MID_STROKE = 3;
const BASE_ARROW_LENGTH = 14;
const BASE_ROUTE_LABEL_FONT = 12;
const BASE_PIN_LABEL_FONT = 11;

/** Pixels before a primary-button drag is treated as map pan (not a pin click). */
const PATROL_PAN_DRAG_THRESHOLD_PX = 6;

const toLocationLabel = (patrolItem, pinData, index) => {
  const raw =
    patrolItem?.editable_location_id ||
    patrolItem?.location_id ||
    pinData?.editable_location_id ||
    pinData?.location_id ||
    patrolItem?.label ||
    pinData?.label;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim();
  }
  return `L${index + 1}`;
};

function PatrolImageViewer({
  imageUrl,
  selectedPatrol,
  currentPinId,
  yaw,
  fov,
  rotationCycleComplete,
  /** 'next' | 'prev' | null – when set, triggers slide same as rotation complete; on complete parent updates index */
  slideDirection,
  onSlideComplete,
  /** Called after smooth pin-click transition completes. */
  onPinTransitionComplete,
  /**
   * Floor-plan slide did not complete (zoom, toolbar). Payload: `fromPinClick`, `fromRotationCycle`.
   * Parent clears latch; resumes 360° only when auto patrol full-turn slide was aborted (`fromRotationCycle`).
   */
  onPatrolSlideAborted,
  /**
   * When the patrol indicator **starts** moving along the route segment (line start / leaves origin pin),
   * parent runs video blend. Payload: `fromIndex`, `toIndex`.
   */
  onPatrolFloorSlideStart,
  /** Zoom UI + pin-centered zoom only when floor plan is maximized and player is fullscreen */
  enableZoom = false,
}) {
  const imageWidth = 1000;
  const imageHeight = 800;

  const [patrolSlideRunId, setPatrolSlideRunId] = useState(0);
  const patrolSlideFrameGetterRef = useRef(() => null);
  const [overrideYaw, setOverrideYaw] = useState(null);
  const containerRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const zoomSliderRef = useRef(null);
  const zoomRef = useRef(1);
  const [layoutZoom, setLayoutZoom] = useState(1);
  const pendingScrollAfterZoomRef = useRef(null);
  const zoomAnimationRef = useRef(null);
  const panAnimationRef = useRef(null);
  const pinClickTargetIndexRef = useRef(null);
  /** Set for any active pin-to-pin slide (click or auto); used to recompute scaled coords when zoom changes. */
  const slideTargetIndexRef = useRef(null);
  /** Start pin when the slide arms; frozen until complete so midpoint video swap cannot desync the path. */
  const slideOriginPinIndexRef = useRef(null);
  /** True if current slide was armed by full 360 rotation complete (not manual next/prev / pin click). */
  const slideFromRotationCycleRef = useRef(false);

  /** Primary-drag or middle-mouse pan on scroll viewport (no toolbar control). */
  const patrolPanListenersRef = useRef({ move: null, up: null });
  const patrolPanOriginRef = useRef({ x: 0, y: 0, sl: 0, st: 0 });
  const patrolPanMovedRef = useRef(false);
  const patrolPanLeftCommittedRef = useRef(false);
  const suppressNextPatrolPinClickRef = useRef(false);
  const [isPatrolMapDragging, setIsPatrolMapDragging] = useState(false);
  /** True while +/- zoom RAF runs (set before first frame). */
  const patrolZoomAnimatingRef = useRef(false);
  /** True while user holds pointer on zoom slider (drag). */
  const patrolZoomSliderDraggingRef = useRef(false);

  // =========================
  // Normalize & Sort Pins
  // =========================
  const normalizedPins = useMemo(() => {
    if (!Array.isArray(selectedPatrol)) return [];

    return selectedPatrol
      .map((p, idx) => {
        if (!p?.pin || !Array.isArray(p.pin)) return null;
        const data = p.pin[0];
        return {
          id: p.pin_id,
          xPercent: data.xPercent,
          yPercent: data.yPercent,
          locationLabel: toLocationLabel(p, data, idx),
          created_at: p.created_at,
        };
      })
      .filter(Boolean);
  }, [selectedPatrol]);

  const sortedPins = useMemo(() => {
    return [...normalizedPins].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  }, [normalizedPins]);

  // =========================
  // Pin Index from Video
  // =========================
  const currentPinIndex = useMemo(() => {
    if (!sortedPins.length) return 0;
    const idx = sortedPins.findIndex((p) => p.id === currentPinId);
    return idx === -1 ? 0 : idx;
  }, [currentPinId, sortedPins]);

  const nextIndex =
    sortedPins.length > 0 ? (currentPinIndex + 1) % sortedPins.length : 0;

  // =========================
  // Helpers
  // =========================
  const pinXYAtZoom = useCallback(
    (index, z) => {
      if (!sortedPins[index]) return { x: 0, y: 0 };
      const zz = Math.max(z, 0.001);
      return {
        x: sortedPins[index].xPercent * imageWidth * zz,
        y: sortedPins[index].yPercent * imageHeight * zz,
      };
    },
    [sortedPins, imageWidth, imageHeight],
  );

  const getPinPosition = (index) => pinXYAtZoom(index, layoutZoom);

  /** Center of zoom = current patrol pin (RotatingCircle). Fallback: image center when no pins. */
  const getZoomAnchorPin = useCallback(() => {
    if (!sortedPins.length) {
      return { x: imageWidth / 2, y: imageHeight / 2 };
    }
    const pin = sortedPins[currentPinIndex];
    if (!pin) return { x: imageWidth / 2, y: imageHeight / 2 };
    return {
      x: pin.xPercent * imageWidth,
      y: pin.yPercent * imageHeight,
    };
  }, [sortedPins, currentPinIndex, imageWidth, imageHeight]);

  const getAngleBetweenPins = (fromIndex, toIndex) =>
    bearingDegForPatrolSegment(sortedPins, fromIndex, toIndex);

  const cancelPatrolPinSlide = useCallback(() => {
    const wasPinClick = pinClickTargetIndexRef.current != null;
    const hadActive =
      slideTargetIndexRef.current != null || wasPinClick;
    const id = panAnimationRef.current;
    if (id != null) {
      cancelAnimationFrame(id);
      panAnimationRef.current = null;
    }
    pinClickTargetIndexRef.current = null;
    slideTargetIndexRef.current = null;
    slideOriginPinIndexRef.current = null;
    setPatrolSlideRunId(0);
    setOverrideYaw(null);
    if (hadActive) {
      onPatrolSlideAborted?.({
        fromPinClick: wasPinClick,
        fromRotationCycle: slideFromRotationCycleRef.current,
      });
    }
    slideFromRotationCycleRef.current = false;
  }, [onPatrolSlideAborted]);

  const isPatrolZoomBusy = useCallback(
    () =>
      patrolZoomAnimatingRef.current ||
      zoomAnimationRef.current != null ||
      patrolZoomSliderDraggingRef.current,
    [],
  );

  const applyZoom = useCallback((zoomValue, pin) => {
    const container = containerRef.current;
    if (!container) return;

    const safeZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomValue));
    const prevZoom = zoomRef.current;

    if (safeZoom !== prevZoom) {
      if (slideTargetIndexRef.current != null) {
        cancelPatrolPinSlide();
      } else {
        setOverrideYaw(null);
      }
    }

    const sliding = slideTargetIndexRef.current != null;

    if (!sliding) {
      const prevAnchorX = pin.x * prevZoom;
      const prevAnchorY = pin.y * prevZoom;
      const viewportX = prevAnchorX - container.scrollLeft;
      const viewportY = prevAnchorY - container.scrollTop;

      pendingScrollAfterZoomRef.current = {
        viewportX,
        viewportY,
        anchorBaseX: pin.x,
        anchorBaseY: pin.y,
        safeZoom,
      };
    } else {
      pendingScrollAfterZoomRef.current = null;
    }

    zoomRef.current = safeZoom;
    setLayoutZoom(safeZoom);
  }, [cancelPatrolPinSlide]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const pending = pendingScrollAfterZoomRef.current;
    if (!container || !pending) return;

    pendingScrollAfterZoomRef.current = null;
    const { viewportX, viewportY, anchorBaseX, anchorBaseY, safeZoom } =
      pending;
    const scaledX = anchorBaseX * safeZoom;
    const scaledY = anchorBaseY * safeZoom;
    container.scrollLeft = scaledX - viewportX;
    container.scrollTop = scaledY - viewportY;
  }, [layoutZoom]);

  const zoomToPin = useCallback(
    (targetZoom) => {
      const container = containerRef.current;
      if (!enableZoom || !container) return;

      const startZoom = zoomRef.current;
      const endZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, targetZoom));
      if (startZoom === endZoom) return;

      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
        patrolZoomAnimatingRef.current = false;
      }

      patrolZoomAnimatingRef.current = true;

      const duration = ZOOM_ANIMATION_DURATION_MS;
      const startTime = performance.now();
      const pin = getZoomAnchorPin();

      const animate = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const currentZoom =
          startZoom + (endZoom - startZoom) * easeInOutSmooth(progress);

        applyZoom(currentZoom, pin);

        if (progress < 1) {
          zoomAnimationRef.current = requestAnimationFrame(animate);
        } else {
          zoomAnimationRef.current = null;
          patrolZoomAnimatingRef.current = false;
          if (zoomSliderRef.current) {
            zoomSliderRef.current.value = String(endZoom);
          }
        }
      };

      zoomAnimationRef.current = requestAnimationFrame(animate);
    },
    [applyZoom, enableZoom, getZoomAnchorPin],
  );

  // =========================
  // Trigger Slide (rotation complete OR manual next/prev)
  // =========================
  const prevIndex =
    sortedPins.length > 0
      ? (currentPinIndex - 1 + sortedPins.length) % sortedPins.length
      : 0;

  useEffect(() => {
    const slideToNext = rotationCycleComplete || slideDirection === "next";
    const slideToPrev = slideDirection === "prev";
    if (!slideToNext && !slideToPrev) return;
    if (sortedPins.length <= 1) return;

    const targetIndex = slideToNext ? nextIndex : prevIndex;
    const angle = getAngleBetweenPins(currentPinIndex, targetIndex);
    setOverrideYaw(angle);

    const timer = setTimeout(() => {
      if (isPatrolZoomBusy()) {
        setOverrideYaw(null);
        onPatrolSlideAborted?.({
          fromPinClick: false,
          fromRotationCycle: Boolean(rotationCycleComplete),
        });
        return;
      }
      slideFromRotationCycleRef.current = Boolean(rotationCycleComplete);
      slideOriginPinIndexRef.current = currentPinIndex;
      slideTargetIndexRef.current = targetIndex;
      setPatrolSlideRunId((n) => n + 1);
    }, 400);

    return () => clearTimeout(timer);
  }, [
    rotationCycleComplete,
    slideDirection,
    currentPinIndex,
    nextIndex,
    prevIndex,
    sortedPins.length,
    isPatrolZoomBusy,
    onPatrolSlideAborted,
  ]);

  const handleMoveComplete = () => {
    const targetIndex = pinClickTargetIndexRef.current;
    if (typeof targetIndex === "number") {
      pinClickTargetIndexRef.current = null;
      slideTargetIndexRef.current = null;
      slideOriginPinIndexRef.current = null;
      slideFromRotationCycleRef.current = false;
      setPatrolSlideRunId(0);
      setOverrideYaw(null);
      onPinTransitionComplete?.(targetIndex);
      return;
    }

    const direction =
      rotationCycleComplete || slideDirection === "next" ? "next" : "prev";
    slideTargetIndexRef.current = null;
    slideOriginPinIndexRef.current = null;
    slideFromRotationCycleRef.current = false;
    setPatrolSlideRunId(0);
    setOverrideYaw(null);
    onSlideComplete?.(direction);
  };

  const handlePinClick = useCallback(
    (targetIndex) => {
      if (!Number.isInteger(targetIndex)) return;
      if (targetIndex === currentPinIndex) return;
      if (targetIndex < 0 || targetIndex >= sortedPins.length) return;
      if (isPatrolZoomBusy()) return;

      const angle = getAngleBetweenPins(currentPinIndex, targetIndex);
      setOverrideYaw(angle);
      pinClickTargetIndexRef.current = targetIndex;
      slideOriginPinIndexRef.current = currentPinIndex;
      slideTargetIndexRef.current = targetIndex;
      slideFromRotationCycleRef.current = false;

      if (panAnimationRef.current) {
        cancelAnimationFrame(panAnimationRef.current);
      }
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
        patrolZoomAnimatingRef.current = false;
      }
      pendingScrollAfterZoomRef.current = null;

      setPatrolSlideRunId((n) => n + 1);
    },
    [currentPinIndex, sortedPins.length, isPatrolZoomBusy],
  );

  const notifyFloorSlideStart = useCallback(() => {
    if (typeof onPatrolFloorSlideStart !== "function") return;
    const fromIdx = slideOriginPinIndexRef.current;
    const toIdx = slideTargetIndexRef.current;
    const n = sortedPins.length;
    if (
      typeof fromIdx !== "number" ||
      typeof toIdx !== "number" ||
      !Number.isInteger(fromIdx) ||
      !Number.isInteger(toIdx) ||
      fromIdx < 0 ||
      fromIdx >= n ||
      toIdx < 0 ||
      toIdx >= n ||
      fromIdx === toIdx
    ) {
      return;
    }
    onPatrolFloorSlideStart({ fromIndex: fromIdx, toIndex: toIdx });
  }, [onPatrolFloorSlideStart, sortedPins.length]);

  // =========================
  // Initial Focus On Current Pin
  // =========================
  useEffect(() => {
    if (!containerRef.current) return;
    if (!sortedPins.length) return;
    if (patrolSlideRunId !== 0) return;

    const container = containerRef.current;
    const pinPos = getPinPosition(currentPinIndex);
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const targetScrollLeft = pinPos.x - containerWidth / 2;
    const targetScrollTop = pinPos.y - containerHeight / 2;

    container.scrollLeft = targetScrollLeft;
    container.scrollTop = targetScrollTop;
  }, [currentPinIndex, sortedPins, patrolSlideRunId]);

  // =========================
  // Auto Pan When Slide Starts
  // =========================
  useEffect(() => {
    if (patrolSlideRunId === 0) return;
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const startScrollLeft = container.scrollLeft;
    const startScrollTop = container.scrollTop;

    const duration = PATROL_SLIDE_DURATION_MS; // must match RotatingCircle moveDuration
    let startTime = null;

    const animatePan = (now) => {
      if (startTime == null) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easeInOutSmooth(progress);

      const targetIdx = slideTargetIndexRef.current;
      if (
        targetIdx == null ||
        !Number.isInteger(targetIdx) ||
        targetIdx < 0 ||
        targetIdx >= sortedPins.length
      ) {
        panAnimationRef.current = null;
        return;
      }

      const z = Math.max(zoomRef.current, 0.001);
      const to = pinXYAtZoom(targetIdx, z);
      const targetScrollLeft = to.x - containerWidth / 2;
      const targetScrollTop = to.y - containerHeight / 2;

      const currentLeft =
        startScrollLeft + (targetScrollLeft - startScrollLeft) * ease;
      const currentTop =
        startScrollTop + (targetScrollTop - startScrollTop) * ease;

      container.scrollLeft = currentLeft;
      container.scrollTop = currentTop;

      if (progress < 1) {
        const id = requestAnimationFrame(animatePan);
        panAnimationRef.current = id;
      } else {
        panAnimationRef.current = null;
      }
    };

    const firstId = requestAnimationFrame(animatePan);
    panAnimationRef.current = firstId;

    return () => {
      const id = panAnimationRef.current;
      if (id != null) cancelAnimationFrame(id);
      panAnimationRef.current = null;
    };
  }, [patrolSlideRunId, sortedPins.length, pinXYAtZoom]);

  useEffect(() => {
    pendingScrollAfterZoomRef.current = null;
    zoomRef.current = 1;
    setLayoutZoom(1);
    if (zoomSliderRef.current) {
      zoomSliderRef.current.value = "1";
    }
    return () => {
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
        patrolZoomAnimatingRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    const endSliderDrag = () => {
      patrolZoomSliderDraggingRef.current = false;
    };
    window.addEventListener("mouseup", endSliderDrag);
    window.addEventListener("touchend", endSliderDrag);
    window.addEventListener("pointerup", endSliderDrag);
    window.addEventListener("pointercancel", endSliderDrag);
    return () => {
      window.removeEventListener("mouseup", endSliderDrag);
      window.removeEventListener("touchend", endSliderDrag);
      window.removeEventListener("pointerup", endSliderDrag);
      window.removeEventListener("pointercancel", endSliderDrag);
    };
  }, []);

  useEffect(() => {
    if (enableZoom) {
      if (zoomSliderRef.current) {
        zoomSliderRef.current.value = String(zoomRef.current);
      }
      return;
    }

    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
      zoomAnimationRef.current = null;
      patrolZoomAnimatingRef.current = false;
    }
    pendingScrollAfterZoomRef.current = null;
    zoomRef.current = 1;
    setLayoutZoom(1);
    if (zoomSliderRef.current) {
      zoomSliderRef.current.value = "1";
    }

    const container = containerRef.current;
    if (container && sortedPins.length) {
      const pin = sortedPins[currentPinIndex];
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      if (pin) {
        container.scrollLeft = pin.xPercent * imageWidth - cw / 2;
        container.scrollTop = pin.yPercent * imageHeight - ch / 2;
      }
    }
  }, [enableZoom, sortedPins, currentPinIndex, imageWidth, imageHeight]);

  const detachPatrolPanListeners = useCallback(() => {
    const { move, up } = patrolPanListenersRef.current;
    if (move) window.removeEventListener("mousemove", move);
    if (up) window.removeEventListener("mouseup", up);
    patrolPanListenersRef.current = { move: null, up: null };
    patrolPanLeftCommittedRef.current = false;
    setIsPatrolMapDragging(false);
    document.body.style.userSelect = "";
  }, []);

  const handlePatrolScrollMouseDown = useCallback(
    (e) => {
      if (!enableZoom) return;
      if (e.button !== 0 && e.button !== 1) return;

      /** Do not attach map-pan listeners on pin markers — keeps pin click + clears stale post-pan suppress. */
      if (e.target?.closest?.("[data-patrol-pin]")) {
        suppressNextPatrolPinClickRef.current = false;
        return;
      }

      const isMiddle = e.button === 1;
      if (isMiddle) e.preventDefault();

      const container = containerRef.current;
      if (!container) return;

      detachPatrolPanListeners();

      patrolPanMovedRef.current = false;
      /** Middle: pan immediately; primary: wait for drag threshold (keeps pin clicks). */
      patrolPanLeftCommittedRef.current = isMiddle;
      patrolPanOriginRef.current = {
        x: e.clientX,
        y: e.clientY,
        sl: container.scrollLeft,
        st: container.scrollTop,
      };

      const onMove = (ev) => {
        const c = containerRef.current;
        if (!c) return;
        const o = patrolPanOriginRef.current;
        const dx = ev.clientX - o.x;
        const dy = ev.clientY - o.y;

        if (!patrolPanLeftCommittedRef.current) {
          if (
            Math.abs(dx) + Math.abs(dy) <
            PATROL_PAN_DRAG_THRESHOLD_PX
          ) {
            return;
          }
          patrolPanLeftCommittedRef.current = true;
          document.body.style.userSelect = "none";
          setIsPatrolMapDragging(true);
        }

        if (Math.abs(dx) + Math.abs(dy) > 2) {
          patrolPanMovedRef.current = true;
        }
        c.scrollLeft = o.sl - dx;
        c.scrollTop = o.st - dy;
      };

      const onUp = () => {
        // Only suppress a possible ghost click after a real pan (crossed drag threshold), not tiny wobble.
        if (
          patrolPanMovedRef.current &&
          patrolPanLeftCommittedRef.current
        ) {
          suppressNextPatrolPinClickRef.current = true;
        }
        detachPatrolPanListeners();
      };

      patrolPanListenersRef.current = { move: onMove, up: onUp };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [enableZoom, detachPatrolPanListeners],
  );

  const onPatrolPinMarkerClick = useCallback(
    (index) => {
      if (suppressNextPatrolPinClickRef.current) {
        suppressNextPatrolPinClickRef.current = false;
        return;
      }
      handlePinClick(index);
    },
    [handlePinClick],
  );

  useEffect(() => {
    if (!enableZoom) {
      detachPatrolPanListeners();
    }
  }, [enableZoom, detachPatrolPanListeners]);

  useEffect(() => () => detachPatrolPanListeners(), [detachPatrolPanListeners]);

  if (!imageUrl) return null;

  const currentPosition = getPinPosition(currentPinIndex);
  const zLayout = Math.max(layoutZoom, 0.001);
  const invZ = 1 / zLayout;
  /** Keep patrol pin marker (orange dot/icon) visually stable for zoom in/out. */
  const pinIconSize = BASE_PIN_SIZE;
  /** Keep route line/dot visual weight stable while zooming patrol drawing. */
  const routeLineStroke = BASE_ROUTE_LINE_STROKE;
  const routeArrowStrokeThin = BASE_ROUTE_ARROW_STROKE_THIN;
  const routeArrowStrokeThick = BASE_ROUTE_ARROW_STROKE_THICK;
  const startDotR = BASE_START_DOT_R;
  const midCircleR = BASE_MID_CIRCLE_R;
  const midStroke = BASE_MID_STROKE;
  const arrowLength = Math.max(4, BASE_ARROW_LENGTH * invZ);
  const routeLabelFontPx = BASE_ROUTE_LABEL_FONT;
  const pinLabelFontPx = BASE_PIN_LABEL_FONT;
  const pinLabelTop = 18;
  const pinLabelMinWidth = 26;
  /** Keep patrol circle overlay visually stable for zoom in/out. */
  const rotatingCircleSize = 60;

  patrolSlideFrameGetterRef.current = () => {
    const originIdx = slideOriginPinIndexRef.current;
    const targetIdx = slideTargetIndexRef.current;
    if (
      originIdx == null ||
      !Number.isInteger(originIdx) ||
      originIdx < 0 ||
      originIdx >= sortedPins.length ||
      targetIdx == null ||
      !Number.isInteger(targetIdx) ||
      targetIdx < 0 ||
      targetIdx >= sortedPins.length
    ) {
      return null;
    }
    const z = Math.max(zoomRef.current, 0.001);
    return {
      start: pinXYAtZoom(originIdx, z),
      end: pinXYAtZoom(targetIdx, z),
    };
  };

  /** Slider follows pointer 1:1; RAF zoom animation restarts every tick and feels janky while dragging. */
  const handleSliderChange = (e) => {
    if (!enableZoom) return;
    const val = Number(e.target.value);
    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
      zoomAnimationRef.current = null;
      patrolZoomAnimatingRef.current = false;
    }
    applyZoom(val, getZoomAnchorPin());
    if (zoomSliderRef.current) {
      zoomSliderRef.current.value = String(val);
    }
  };

  const zoomOutStep = () => {
    if (!enableZoom) return;
    const nextZoom = Math.max(ZOOM_MIN, zoomRef.current - ZOOM_STEP);
    if (zoomSliderRef.current) {
      zoomSliderRef.current.value = String(nextZoom);
    }
    zoomToPin(nextZoom);
  };

  const zoomInStep = () => {
    if (!enableZoom) return;
    const nextZoom = Math.min(ZOOM_MAX, zoomRef.current + ZOOM_STEP);
    if (zoomSliderRef.current) {
      zoomSliderRef.current.value = String(nextZoom);
    }
    zoomToPin(nextZoom);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: "relative",
      }}
    >
      <Box
        ref={containerRef}
        onMouseDown={enableZoom ? handlePatrolScrollMouseDown : undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "auto",
          display: "flex",
          scrollBehavior: "auto",
          cursor:
            enableZoom && isPatrolMapDragging ? "grabbing" : "default",
        }}
      >
        <Box
          ref={imageWrapperRef}
          sx={{
            position: "relative",
            width: imageWidth * zLayout,
            height: imageHeight * zLayout,
            margin: "auto",
            flexShrink: 0,
            overflow: "visible",
          }}
        >
          <img
            src={imageUrl}
            alt="floor"
            draggable={false}
            style={{ width: "100%", height: "100%" }}
          />

          {sortedPins.length > 1 && (
            <svg
              width={imageWidth * zLayout}
              height={imageHeight * zLayout}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              {sortedPins.map((pin, index) => {
                const from = getPinPosition(index);
                const to = getPinPosition((index + 1) % sortedPins.length);

                const dx = to.x - from.x;
                const dy = to.y - from.y;
                const length = Math.sqrt(dx * dx + dy * dy) || 1;

                const unitX = dx / length;
                const unitY = dy / length;

                const desiredGap = pinIconSize / 2 + 2 * invZ;
                const circleGap = Math.min(
                  desiredGap,
                  Math.max(0, length / 2 - 0.5),
                );
                const startX = from.x + unitX * circleGap;
                const startY = from.y + unitY * circleGap;

                const endX = to.x - unitX * circleGap;
                const endY = to.y - unitY * circleGap;

                const arrowLenClamped = Math.min(arrowLength, length / 2);
                const arrowAngle = 25 * (Math.PI / 180);
                const angle = Math.atan2(dy, dx);

                const arrowX1 =
                  endX - arrowLenClamped * Math.cos(angle - arrowAngle);
                const arrowY1 =
                  endY - arrowLenClamped * Math.sin(angle - arrowAngle);

                const arrowX2 =
                  endX - arrowLenClamped * Math.cos(angle + arrowAngle);
                const arrowY2 =
                  endY - arrowLenClamped * Math.sin(angle + arrowAngle);

                const midX = (startX + endX) / 2;
                const midY = (startY + endY) / 2;

                return (
                  <g key={pin.id}>
                    <line
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      stroke="#1BC800"
                      strokeWidth={routeLineStroke}
                      strokeLinecap="round"
                    />
                    <circle cx={startX} cy={startY} r={startDotR} fill="#1BC800" />
                    <line
                      x1={endX}
                      y1={endY}
                      x2={arrowX1}
                      y2={arrowY1}
                      stroke="#1BC800"
                      strokeWidth={routeArrowStrokeThin}
                      strokeLinecap="round"
                    />
                    <line
                      x1={endX}
                      y1={endY}
                      x2={arrowX2}
                      y2={arrowY2}
                      stroke="#1BC800"
                      strokeWidth={routeArrowStrokeThick}
                      strokeLinecap="round"
                    />
                    <circle
                      cx={midX}
                      cy={midY}
                      r={midCircleR}
                      fill="#ffffff"
                      stroke="#1BC800"
                      strokeWidth={midStroke}
                    />
                    <text
                      x={midX}
                      y={midY + 5}
                      textAnchor="middle"
                      fontSize={routeLabelFontPx}
                      fontWeight="bold"
                      fill="#000"
                    >
                      {index + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}

          {sortedPins.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 4,
                overflow: "visible",
              }}
            >
              <RotatingCircle
                x={currentPosition.x}
                y={currentPosition.y}
                slideRunId={patrolSlideRunId}
                slideEndpointsGetterRef={patrolSlideFrameGetterRef}
                isActive={true}
                onMoveComplete={handleMoveComplete}
                onSlideStart={
                  typeof onPatrolFloorSlideStart === "function"
                    ? notifyFloorSlideStart
                    : undefined
                }
                yaw={overrideYaw ?? yaw}
                fov={fov}
                moveDuration={PATROL_SLIDE_DURATION_MS}
                size={rotatingCircleSize}
              />
            </Box>
          )}

          {sortedPins.map((pin, index) => {
            const pos = getPinPosition(index);
            return (
              <Box
                key={pin.id}
                data-patrol-pin
                onClick={() => onPatrolPinMarkerClick(index)}
                sx={{
                  position: "absolute",
                  left: pos.x,
                  top: pos.y,
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                  cursor: "pointer",
                }}
              >
                <img
                  src="/images/pin.svg"
                  alt="pin"
                  width={pinIconSize}
                  height={pinIconSize}
                />
                <Typography
                  sx={{
                    position: "absolute",
                    top: pinLabelTop,
                    left: "50%",
                    transform: "translateX(-50%)",
                    minWidth: pinLabelMinWidth,
                    px: 0.75,
                    py: 0.15,
                    borderRadius: "6px",
                    border: "2px solid #54C600",
                    bgcolor: "#FFFFFF",
                    color: "#111827",
                    fontSize: `${pinLabelFontPx}px`,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    zIndex: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                  }}
                >
                  {pin.locationLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {enableZoom && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          onMouseDown={() => {
            patrolZoomSliderDraggingRef.current = true;
            cancelPatrolPinSlide();
          }}
          onTouchStart={() => {
            patrolZoomSliderDraggingRef.current = true;
            cancelPatrolPinSlide();
          }}
          sx={{
            flexShrink: 0,
            alignSelf: "flex-start",
            mt: 0.5,
            ml: 0.5,
            mb: 0.5,
            bgcolor: "rgba(0, 0, 0, 0.55)",
            borderRadius: 1,
            px: 0.75,
            py: 0.5,
            zIndex: 20,
          }}
        >
          <IconButton
            size="small"
            onClick={zoomOutStep}
            sx={{ color: "#fff" }}
            title="Zoom out"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Box
            component="input"
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={SLIDER_STEP}
            defaultValue={1}
            ref={zoomSliderRef}
            onChange={handleSliderChange}
            sx={{
              width: 110,
              accentColor: "#1BC800",
              cursor: "pointer",
              bgcolor: "transparent",
            }}
          />
          <IconButton
            size="small"
            onClick={zoomInStep}
            sx={{ color: "#fff" }}
            title="Zoom in"
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: 700,
              minWidth: 56,
              textAlign: "center",
              userSelect: "none",
            }}
          >
            {`${zLayout.toFixed(2)}x`}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default PatrolImageViewer;
